// ─────────────────────────────────────────────────────────────
// Measurement Protocol — TAB 5: QUESTIONNAIRES
//
// Moved verbatim out of the original monolithic MPCreationIndex.js.
// No fields are marked mandatory here either, so validate() always
// passes — wired up the same way as the chain tabs for consistency.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch } from "react-redux";
import {
  toArray,
  eiCls,
  Section,
  TD,
  EmailPreviewModal,
  Toast,
  FormActions,
} from "../shared/mpShared";
import {
  getCompanyID,
  getEmployeeID,
  hasPrivilege,
  PRIVILEGES,
} from "../../../Common/UIComponents";
import {
  MP_GetQuestionariesByID,
  MP_SaveQuestionaires,
} from "../../../API_Service/MeasurementProtocol_Slice";

const QuestionnairesTab = forwardRef(function QuestionnairesTab(
  { mpID, mp, onSaved, onSubmitAll },
  ref,
) {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [previewOpen, setPreviewOpen] = useState(false);

  const notify = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 2500);
  };

  const upd = (idx, k, v) =>
    setRows((r) => r?.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

  const getPreviewRows = () =>
    rows
      .filter((r) => r.text || r.remarks)
      .map((r) => ({
        label: r.Checklist,
        value: [r.text, r.remarks].filter(Boolean).join(" — "),
      }));

  const handleSave = () =>
    new Promise((resolve, reject) => {
      try {
        var QuestionairesDetails = [];
        rows.map((r) => {
          const Questions = {
            MP_CheckList_Id: r?.mPCheckListID,
            Checklist_Id: r?.checklistID,
            Checklist_Category_Id: r?.checklistCategoryID,
            Checklist_Category_Name: r?.checklistCategoryName,
            Checklist_Answer: r?.checklistAnswer,
            Remarks: r?.remarks,
            Texts: r?.text,
            Checklist: r?.checklist,
          };
          QuestionairesDetails.push(Questions);
        });
        const payload = {
          MPID: mpID,
          QuestionairesDetails: QuestionairesDetails,
          SavedBy: getEmployeeID(),
          CompanyID: getCompanyID(),
        };
        dispatch(MP_SaveQuestionaires(payload))
          .then((res) => {
            if (res?.payload?.data > 0) {
              setSaving(true);
              GetQuestionaries();
              setTimeout(() => {
                setSaving(false);
                notify("Questionnaire saved!");
                setTimeout(() => {
                  onSaved?.();
                  resolve(true);
                }, 800);
              }, 800);
            } else {
              notify("Couldn't save the questionnaire.", "error");
              reject(new Error("Save returned no rows affected"));
            }
          })
          .catch((err) => {
            console.error("Error saving questionnaire:", err);
            setSaving(false);
            notify("An error occurred while saving.", "error");
            reject(err);
          });
      } catch (err) {
        console.error("Error saving questionnaire:", err);
        setSaving(false);
        notify("An error occurred while saving.", "error");
        reject(err);
      }
    });

  const validate = () => ({ valid: true, field: null });
  const focusField = () => {};

  useImperativeHandle(ref, () => ({
    validate,
    focusField,
    getPreviewRows,
    save: handleSave,
  }));

  useEffect(() => {
    GetQuestionaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GetQuestionaries = () => {
    const payload = {
      mpid: mpID,
      companyID: getCompanyID(),
    };
    dispatch(MP_GetQuestionariesByID(payload))
      .then((res) => {
        const data = toArray(res?.payload?.data);
        if (data.length) {
          setRows(
            data.map((q) => ({
              checklistID: q.Checklist_Id,
              checklist: q?.Checklist,
              mPCheckListID: q?.MP_CheckList_Id,
              checklistCategoryID: q?.Checklist_Category_Id,
              checklistCategoryName: q?.Checklist_Category_Name,
              checklistAnswer: q?.Checklist_Answer,
              remarks: q?.Remarks,
              text: q?.Texts,
            })),
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching questionnaire details:", err);
        notify("Couldn't load the questionnaire. Please refresh.", "error");
      });
  };

  return (
    <>
      <Toast msg={toast.msg} show={toast.show} type={toast.type} />
      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mp={mp}
        subject={`Questionnaire Update — ${mp?.MPNo ?? "New MP"}`}
        rows={getPreviewRows()}
      />
      <div className="space-y-4 min-w-0">
        <Section icon="quiz" title="Checklist Points">
          <div className="rounded-xl border border-[#e5e9e9] w-full max-w-full overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-[11.5px]">
              <thead>
                <tr>
                  {["Checklist Points", "Text", "Remarks"]?.map((h) => (
                    <th
                      key={h}
                      className="bg-[#f1f4f4] px-4 py-3 text-left text-[#3e494a] font-bold text-[11px] uppercase tracking-wider border-b border-[#ebeeee]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows?.map((row, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-[#fafcfc]"}
                  >
                    <TD
                      style={{
                        maxWidth: 320,
                        fontWeight: 500,
                        color: "#3e494a",
                      }}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: row?.checklist || "",
                        }}
                      />
                    </TD>
                    <TD style={{ minWidth: 200 }}>
                      <input
                        className={eiCls}
                        placeholder="Enter text…"
                        value={row?.text}
                        onChange={(e) => upd(idx, "text", e.target.value)}
                      />
                    </TD>
                    <TD style={{ minWidth: 200 }}>
                      <input
                        className={eiCls}
                        placeholder="Remarks…"
                        value={row?.remarks}
                        onChange={(e) => upd(idx, "remarks", e.target.value)}
                      />
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        {hasPrivilege(PRIVILEGES.MP_EDIT) && (
          <FormActions
            onSave={handleSave}
            saving={saving}
            onPreview={() => setPreviewOpen(true)}
            onSubmit={onSubmitAll}
          />
        )}
      </div>
    </>
  );
});

// ─────────────────────────────────────────────────────────────

export default QuestionnairesTab;
export { QuestionnairesTab };
